// 牌桌实体：场地卡、完整角色卡、装备卡与带玩家主题色的棋子。
import { useState } from "react";
import { AnimatedNumber } from "./animated-number.tsx";
import { CardBack, CardFace, ReviewedText, cardFallback, cardImage, sceneImage, tip } from "./assets.tsx";
import { GLOSSARY, PLACE_EFFECT, cardCopy, roleAbilityTip, roleCopy } from "./glossary.ts";
import { dropTap, playerDropId, useDrag } from "./drag.tsx";
import { campAccent, cardAccent, placeAccent, playerTheme, playerThemeStyle } from "./themes.ts";
import { PLACE_STYLE, campLabel } from "./types.ts";
import type { PlaceState, PlayerState, RoleCard } from "./types.ts";
import type { ObjectAction } from "./actions.tsx";

const KANJI_DIGITS = ["", "一", "二", "三", "四", "五", "六", "七", "八", "九", "十"];

export function kanjiNumber(value: number): string {
  return KANJI_DIGITS[value] ?? String(value);
}

export function PlaceCard({
  place,
  occupants,
  playerIndex,
  current,
  droppable,
  onDropTap,
  onPick,
  pickable,
  actions,
  onAction,
  playerActions,
  onPlayerAction,
  onPlayerPick,
  movingPlayer,
}: {
  place: PlaceState;
  occupants: PlayerState[];
  playerIndex: ReadonlyMap<string, number>;
  current?: boolean | undefined;
  droppable?: boolean | undefined;
  onDropTap?: (() => void) | undefined;
  pickable?: boolean | undefined;
  onPick?: (() => void) | undefined;
  actions: ObjectAction[];
  onAction: (action: ObjectAction) => void;
  playerActions: ReadonlyMap<string, ObjectAction[]>;
  onPlayerAction: (action: ObjectAction) => void;
  onPlayerPick?: ((playerId: string) => void) | undefined;
  movingPlayer?: string | undefined;
}) {
  const drag = useDrag();
  const style = PLACE_STYLE[place.place] ?? { kana: "" };
  const effect = PLACE_EFFECT[place.place] ?? "場所行動なし";
  const image = sceneImage(place.place) ?? "";
  return (
    <article
      {...tip({
        title: place.name,
        kicker: `第 ${kanjiNumber(place.slot + 1)} 场`,
        summary: `骰子合计 ${place.numbers.join("、")} 时移动到这里`,
        detail: `场所行动\n${effect}${actions.length > 0 ? `\n\n当前可执行\n${actions.map((action) => action.label).join("、")}` : ""}\n\n相邻规则\n${GLOSSARY.adjacent}`,
        image,
        color: placeAccent(place.place),
      })}
      className={[
        "place-card",
        `place-${place.place}`,
        current ? "is-current" : "",
        pickable || actions.length > 0 ? "is-pickable" : "",
        droppable ? "is-droppable" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-drop={placeDropKey(place)}
      onClick={() => {
        if (droppable && onDropTap) onDropTap();
        else if (pickable && onPick) onPick();
        else if (actions.length === 1) onAction(actions[0]!);
      }}
      role={pickable || droppable || actions.length > 0 ? "button" : undefined}
    >
      <div className="place-scene" aria-hidden="true">
        <img className="place-scene-padding" src={image} alt="" draggable={false} />
        <img src={image} alt="" draggable={false} />
      </div>
      <header className="place-head">
        <span className="place-index">第{kanjiNumber(place.slot + 1)}場</span>
        <h3 className="place-title">
          <ruby>
            {place.name}
            <rt>{style.kana}</rt>
          </ruby>
        </h3>
        <div className="place-dice" aria-label={`到达点数：${place.numbers.join("、")}`}>
          {place.numbers.map((number) => (
            <span
              className="die"
              key={number}
              {...tip({
                title: `${number} 点`,
                kicker: "到达骰目",
                summary: GLOSSARY.dice,
                color: placeAccent(place.place),
              })}
            >
              {number}
            </span>
          ))}
        </div>
      </header>

      <p className="place-effect-caption" {...tip({ title: "場所行動", summary: effect })}>
        <span aria-hidden="true">◆</span>
        {effect}
      </p>
      {actions.length > 1 && (
        <div className="object-choices place-object-choices" aria-label={`${place.name}的可执行行动`}>
          {actions.map((action) => (
            <button
              type="button"
              key={`${action.label}-${JSON.stringify(action.action)}`}
              onClick={(event) => {
                event.stopPropagation();
                onAction(action);
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      <div className="place-pieces" aria-label={`${place.name}的玩家`}>
        {occupants.map((player) => {
          const index = playerIndex.get(player.id) ?? 0;
          const theme = playerTheme(index);
          const reviewedRole = player.role ? roleCopy(player.role.id) : undefined;
          const direct = playerActions.get(player.id) ?? [];
          const dropId = playerDropId(player.id);
          const playerDroppable = drag?.payload.actions.has(dropId) ?? false;
          return (
            <button
              type="button"
              key={player.id}
              data-player-id={player.id}
              data-drop={playerDroppable ? dropId : undefined}
              style={playerThemeStyle(index)}
              className={[
                "player-piece",
                player.alive ? "" : "is-fallen",
                player.id === movingPlayer ? "is-moving" : "",
                player.attack_immune_until_move ? "is-immune" : "",
                direct.length > 0 || playerDroppable ? "is-targetable" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={(event) => {
                event.stopPropagation();
                if (playerDroppable) dropTap(dropId);
                else if (droppable && onDropTap) onDropTap();
                else if (pickable && onPick) onPick();
                else if (direct.length > 0) onPlayerAction(direct[0]!);
                else onPlayerPick?.(player.id);
              }}
              {...tip({
                title: player.name,
                kicker: `${theme.name}棋子`,
                summary: `伤害 ${player.damage}${player.hp === undefined ? "" : ` / ${player.hp}`}${
                  player.attack_immune_until_move ? " · 攻击免疫" : ""
                }`,
                detail: `${
                  player.attack_immune_until_move ? "三月精防护：不受攻击伤害，直到本人下次开始移动。\n" : ""
                }${
                  direct.length > 0
                    ? `当前可直接点击：${direct.map((action) => action.label).join("、")}`
                    : `${
                        player.revealed && player.role
                          ? `${reviewedRole?.name ?? player.role.name} · ${campLabel(player.role.camp)}`
                          : "角色卡尚未公开"
                      }\n点击棋子查看该玩家的公开角色卡、装备与所在场地。`
                }`,
                color: player.revealed && player.role ? campAccent(player.role.camp) : theme.color,
              })}
            >
              <span className="piece-glyph" aria-hidden="true">{theme.piece}</span>
              <span className="piece-name">{player.name}</span>
              <span className="piece-damage"><AnimatedNumber value={player.damage} increaseIsGood={false} /></span>
              {player.attack_immune_until_move && (
                <span className="piece-immunity" aria-label="攻击免疫至下次移动">免</span>
              )}
            </button>
          );
        })}
      </div>
    </article>
  );
}

function placeDropKey(place: PlaceState): string {
  return `place:${place.slot}`;
}

export function RoleCardView({
  role,
  hidden,
  owner,
}: {
  role: RoleCard | null;
  hidden: boolean;
  owner: string;
}) {
  if (!role || hidden) {
    return (
      <div
        className="role-card is-sealed"
        {...tip({
          title: `${owner}的隐藏角色卡`,
          kicker: "身份未明",
          summary: GLOSSARY.hidden,
          color: "#c9a65a",
        })}
      >
        <CardBack className="role-inspector-back" />
      </div>
    );
  }
  const reviewed = roleCopy(role.id);
  const name = reviewed?.name ?? role.name;
  const hp = reviewed?.hp ?? role.hp;
  const winCondition = reviewed?.winCondition ?? role.win_condition;
  return (
    <figure
      className={`role-card camp-${role.camp} has-face`}
      {...tip({
        title: name,
        kicker: `${campLabel(role.camp)} · 体力 ${hp}`,
        summary: reviewed ? undefined : winCondition,
        detail: reviewed ? undefined : roleAbilityTip(role.id),
        titleRuns: reviewed?.nameRuns,
        reviewLines: reviewed?.lines,
        image: `/cards/${role.id}.webp`,
        color: campAccent(role.camp),
      })}
    >
      <CardFace id={role.id} alt={`${name} 的完整角色卡`} />
      <figcaption>
        <span className="camp-tag">{campLabel(role.camp)}</span>
        <span className="role-name">
          {reviewed ? <ReviewedText lines={[reviewed.nameRuns]} /> : name}
        </span>
        <span className="role-hp">体力 {hp}</span>
      </figcaption>
      {reviewed && <ReviewedText lines={reviewed.lines} className="role-reviewed-intro" />}
    </figure>
  );
}

export function PlayerSeal({
  player,
  themeIndex,
  isTurn,
  isMe,
  privateRole,
  droppable,
  onDropTap,
  onDragStart,
  onReveal,
}: {
  player: PlayerState;
  themeIndex: number;
  isTurn?: boolean | undefined;
  isMe?: boolean | undefined;
  privateRole?: RoleCard | null | undefined;
  droppable?: boolean | undefined;
  onDropTap?: (() => void) | undefined;
  onDragStart?: ((event: React.PointerEvent) => void) | undefined;
  onReveal?: (() => void) | undefined;
}) {
  const theme = playerTheme(themeIndex);
  const revealedRole = player.revealed ? player.role : null;
  const tooltipRole = revealedRole ?? (isMe ? privateRole : null);
  const reviewedRole = tooltipRole ? roleCopy(tooltipRole.id) : undefined;
  const accent = tooltipRole ? campAccent(tooltipRole.camp) : theme.color;
  const maxHp = player.hp ?? (isMe ? privateRole?.hp : undefined);
  const remainingHp = maxHp === undefined ? null : Math.max(0, maxHp - player.damage);
  const roleIsPrivate = Boolean(tooltipRole && revealedRole === null);
  const canReveal = Boolean(isMe && !player.revealed && onReveal);
  const roleDetail = tooltipRole
    ? roleAbilityTip(tooltipRole.id)
    : `${GLOSSARY.hidden}\n身份公开前不会泄露角色卡与技能。`;
  const detail = `${roleDetail}${
    player.attack_immune_until_move ? "\n\n三月精防护：不受攻击伤害，直到本人下次开始移动。" : ""
  }`;
  return (
    <button
      type="button"
      aria-label={`${player.name}的角色卡，${remainingHp === null ? "" : `剩余体力 ${remainingHp}，`}累计伤害 ${player.damage}`}
      style={{ ...playerThemeStyle(themeIndex), "--card-accent": accent } as React.CSSProperties}
      onPointerDown={onDragStart}
      className={[
        "player-seal",
        revealedRole ? `is-revealed camp-${revealedRole.camp}` : "is-hidden",
        player.alive ? "" : "is-fallen",
        isTurn ? "is-turn" : "",
        isMe ? "is-me" : "",
        player.attack_immune_until_move ? "is-immune" : "",
        droppable ? "is-droppable" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-drop={playerDropId(player.id)}
      onClick={
        droppable && onDropTap
          ? (event) => {
              event.stopPropagation();
              onDropTap();
            }
          : undefined
      }
      onDoubleClick={
        canReveal
          ? (event) => {
              event.stopPropagation();
              onReveal?.();
            }
          : undefined
      }
      onKeyDown={
        canReveal
          ? (event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              event.stopPropagation();
              if (!event.repeat) onReveal?.();
            }
          : undefined
      }
      aria-description={canReveal ? "双击角色卡或按回车公开身份" : undefined}
      {...tip({
        title: reviewedRole?.name ?? tooltipRole?.name ?? `${player.name}的隐藏角色卡`,
        kicker: tooltipRole
          ? `${campLabel(tooltipRole.camp)} · ${player.name}${roleIsPrivate ? " · 仅自己可见" : ""}`
          : `${theme.name}${isMe ? " · 自分" : ""}`,
        summary: `${roleIsPrivate ? "身份尚未公开 · " : ""}${remainingHp === null ? `累计伤害 ${player.damage}` : `剩余体力 ${remainingHp} / ${player.hp}`}${player.equipment.length > 0 ? ` · 装备 ${player.equipment.length}` : ""}${player.attack_immune_until_move ? " · 攻击免疫" : ""}`,
        detail: reviewedRole
          ? player.attack_immune_until_move
            ? "三月精防护：不受攻击伤害，直到本人下次开始移动。"
            : undefined
          : detail,
        meta: reviewedRole ? undefined : roleIsPrivate ? privateRole?.win_condition : undefined,
        titleRuns: reviewedRole?.nameRuns,
        reviewLines: reviewedRole?.lines,
        image: tooltipRole ? `/cards/${tooltipRole.id}.webp` : undefined,
        color: accent,
        layout: "card",
        detailLabel: tooltipRole ? "角色技能" : "隐藏身份",
        metaLabel: "胜利条件",
      })}
    >
      <span className="player-card-media" aria-hidden="true">
        <span className="player-card-back"><CardBack /></span>
        <span className="player-card-front">
          {tooltipRole && <CardFace id={tooltipRole.id} alt="" />}
        </span>
      </span>
      <span className="player-card-name">
        <strong>{player.name}</strong>
        {isMe && <small>自分</small>}
      </span>
      <span
        className="player-vitals"
        aria-label={
          remainingHp === null
            ? `累计伤害 ${player.damage}`
            : `剩余体力 ${remainingHp}，累计伤害 ${player.damage}`
        }
      >
        {remainingHp !== null && (
          <span className="player-health">
            <strong><AnimatedNumber value={remainingHp} /></strong>
            <small>残</small>
          </span>
        )}
        <span className="player-damage">
          <strong><AnimatedNumber value={player.damage} increaseIsGood={false} /></strong>
          <small>傷</small>
        </span>
      </span>
      {player.equipment.length > 0 && (
        <span className="player-equipment-count" aria-label={`${player.equipment.length} 件装备`}>
          装 <AnimatedNumber value={player.equipment.length} />
        </span>
      )}
      {player.attack_immune_until_move && (
        <span className="player-immunity" aria-label="攻击免疫至下次移动">免</span>
      )}
      {isTurn && <span className="seal-turn">手番</span>}
    </button>
  );
}

export function EquipmentCard({
  id,
  label,
  tilt,
  owner,
  droppable,
  onDropTap,
  onDragStart,
}: {
  id: string;
  label: string;
  tilt?: number | undefined;
  owner?: string | undefined;
  droppable?: boolean | undefined;
  onDropTap?: (() => void) | undefined;
  onDragStart?: ((event: React.PointerEvent) => void) | undefined;
}) {
  const [restingTilt] = useState(() => Math.random() * 12 - 6);
  const image = cardImage(id);
  const reviewed = cardCopy(id);
  return (
    <figure
      style={{ ["--tilt" as string]: `${tilt ?? restingTilt}deg` }}
      data-equipment-card={id}
      data-card-owner={owner}
      {...tip({
        title: label,
        kicker: "完整实体卡面",
        summary: reviewed ? undefined : "效果尚未公开。",
        detail: "当前存在合法目标时，直接把卡拖到高亮的玩家或区域即可执行。",
        titleRuns: reviewed?.nameRuns,
        reviewLines: reviewed?.lines,
        image: image ?? undefined,
        color: cardAccent(id),
      })}
      className={`equip-card${droppable ? " is-droppable" : ""}${onDragStart ? " is-draggable" : ""}`}
      onPointerDown={onDragStart}
      onClick={droppable ? onDropTap : undefined}
    >
      {image ? (
        <img
          src={image}
          alt={`${label} 完整卡面`}
          onError={cardFallback}
          draggable={false}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <span className="equip-text">{label}</span>
      )}
      <figcaption>{label}</figcaption>
    </figure>
  );
}
